import { useMemo } from "react";
import { useIntl } from "react-intl";
import { GlobalActionKey } from "../domain/globalAction.types";
import {
  AccommodationSortingCriteria,
  PriceCalendarMapping,
  ShowExtrasConfigurationMapping,
} from "../../../behaviour/domain/behaviour.entity";
import {
  EngineDisplayModeType,
  EngineResultsFormatType,
  EngineBookingAdvantagesPositionType,
  EngineRoundType,
} from "../../../../gen/witbooking/backoffice/propertyconfiguration/v1/propertyconfiguration_api_pb";
import { BookAndJoinType } from "../../../../gen/witbooking/backoffice/loyalty/brand/v1/loyalty_brand_api_pb";
import { enumOptionsMessages as messages } from "./enumOptions.messages";
import {
  BookAndJoinTypeMapping,
  BookingAdvantagesPositionMapping,
  DisplayModeMapping,
  ResultsFormatTypeMapping,
  RoundTypeMapping,
} from "./enumOptions.mappings";

export interface EnumOption {
  value: string;
  label: string;
}

export function useEnumOptions() {
  const intl = useIntl();

  const getEnumOptions = useMemo(() => {
    return (key: GlobalActionKey): EnumOption[] => {
      switch (key) {
        case "displayMode":
          return [
            {
              value: DisplayModeMapping[EngineDisplayModeType.SIDEBAR],
              label: intl.formatMessage(messages.displayModeSidebar),
            },
            {
              value: DisplayModeMapping[EngineDisplayModeType.EXTENDED],
              label: intl.formatMessage(messages.displayModeExtended),
            },
          ];
        case "accommodationResultsFormat":
          return [
            {
              value: ResultsFormatTypeMapping[EngineResultsFormatType.AUTO],
              label: intl.formatMessage(messages.resultsFormatAuto),
            },
            {
              value: ResultsFormatTypeMapping[EngineResultsFormatType.GRID],
              label: intl.formatMessage(messages.resultsFormatGrid),
            },
            {
              value: ResultsFormatTypeMapping[EngineResultsFormatType.LIST],
              label: intl.formatMessage(messages.resultsFormatList),
            },
            {
              value: ResultsFormatTypeMapping[EngineResultsFormatType.UNIFIED],
              label: intl.formatMessage(messages.resultsFormatUnified),
            },
          ];
        case "accommodationSortingCriteria":
          return [
            {
              value: AccommodationSortingCriteria.ASCENDING,
              label: intl.formatMessage(messages.accommodationsOrderAscending),
            },
            {
              value: AccommodationSortingCriteria.DESCENDING,
              label: intl.formatMessage(messages.accommodationsOrderDescending),
            },
            {
              value: AccommodationSortingCriteria.NO_SORTING,
              label: intl.formatMessage(messages.accommodationsOrderNoSorting),
            },
          ];
        case "bookingAdvantagesPosition":
          return [
            {
              value: BookingAdvantagesPositionMapping[EngineBookingAdvantagesPositionType.TOP],
              label: intl.formatMessage(messages.bookingAdvantagesTop),
            },
            {
              value: BookingAdvantagesPositionMapping[EngineBookingAdvantagesPositionType.BOTTOM],
              label: intl.formatMessage(messages.bookingAdvantagesBottom),
            },
          ];
        case "showExtrasConfiguration":
          return [
            {
              value: ShowExtrasConfigurationMapping.SHOW_EXTRAS_BEFORE_CHECKOUT,
              label: intl.formatMessage(messages.servicesExpanded),
            },
            {
              value: ShowExtrasConfigurationMapping.SHOW_EXTRAS_BEFORE_CHECKOUT_DESKTOP,
              label: intl.formatMessage(messages.servicesExpandedDesktop),
            },
            {
              value: ShowExtrasConfigurationMapping.HIDE_EXTRAS_BEFORE_CHECKOUT,
              label: intl.formatMessage(messages.servicesCollapsed),
            },
          ];
        case "priceCalendar":
          return [
            {
              value: PriceCalendarMapping.SHOW_ALL_PRICES,
              label: intl.formatMessage(messages.priceCalendarShowAll),
            },
            {
              value: PriceCalendarMapping.SHOW_SELECTED_DATE_PRICES,
              label: intl.formatMessage(messages.priceCalendarShowSelected),
            },
            {
              value: PriceCalendarMapping.HIDE_ALL_PRICES,
              label: intl.formatMessage(messages.priceCalendarHideAll),
            },
          ];
        case "bookAndJoinType":
          return [
            {
              value: BookAndJoinTypeMapping[BookAndJoinType.DISABLED],
              label: intl.formatMessage(messages.bookAndJoinDisabled),
            },
            {
              value: BookAndJoinTypeMapping[BookAndJoinType.OPT_IN],
              label: intl.formatMessage(messages.bookAndJoinOptIn),
            },
            {
              value: BookAndJoinTypeMapping[BookAndJoinType.OPT_OUT],
              label: intl.formatMessage(messages.bookAndJoinOptOut),
            },
          ];
        case "buttonRoundType":
        case "tagRoundType":
        case "bookingFormRoundType":
        case "cardRoundType":
        case "formRoundType":
          return [
            {
              value: RoundTypeMapping[EngineRoundType.ZERO],
              label: intl.formatMessage(messages.roundTypeZero, { value: EngineRoundType.ZERO }),
            },
            {
              value: RoundTypeMapping[EngineRoundType.DEFAULT],
              label: intl.formatMessage(messages.roundTypeDefault, {
                value: EngineRoundType.DEFAULT,
              }),
            },
            {
              value: RoundTypeMapping[EngineRoundType.MD],
              label: intl.formatMessage(messages.roundTypeMd, { value: EngineRoundType.MD }),
            },
            {
              value: RoundTypeMapping[EngineRoundType.LG],
              label: intl.formatMessage(messages.roundTypeLg, { value: EngineRoundType.LG }),
            },
            {
              value: RoundTypeMapping[EngineRoundType.XL],
              label: intl.formatMessage(messages.roundTypeXl, { value: EngineRoundType.XL }),
            },
            {
              value: RoundTypeMapping[EngineRoundType.XXL],
              label: intl.formatMessage(messages.roundTypeXxl, { value: EngineRoundType.XXL }),
            },
            {
              value: RoundTypeMapping[EngineRoundType.XXXL],
              label: intl.formatMessage(messages.roundTypeXxxl, { value: EngineRoundType.XXXL }),
            },
            {
              value: RoundTypeMapping[EngineRoundType.FULL],
              label: intl.formatMessage(messages.roundTypeFull, { value: EngineRoundType.FULL }),
            },
          ];
        default:
          return [];
      }
    };
  }, [intl]);

  const getEnumLabel = useMemo(() => {
    return (key: GlobalActionKey, value: string): string | undefined => {
      const options = getEnumOptions(key);
      return options.find(opt => opt.value === value)?.label;
    };
  }, [getEnumOptions]);

  return { getEnumOptions, getEnumLabel };
}
